import { useRef, type ChangeEvent, type FormEvent, type RefObject } from 'react';

import { cn } from '@/lib/utils';
import { useAutoGrowTextarea } from '../../hooks/use-autogrow-textarea';
import { composerKeyHandler } from '../../lib/composer-keys';
import { DOC_MIMES, IMAGE_MIMES, type PendingAtt } from './attachments';
import { PendingThumb } from './pending-thumb';

const COMPOSER_MAX_HEIGHT_PX = 168;

const voiceBtnBase =
  'flex-none w-[44px] h-[44px] p-0 grid place-items-center box-border bg-card text-foreground border border-border rounded-sm text-[17px] cursor-pointer font-[inherit]';
const attachBtnBase =
  'flex-none w-[44px] h-[44px] p-0 grid place-items-center box-border bg-card border border-border rounded-sm text-[17px] cursor-pointer font-[inherit] text-foreground disabled:cursor-not-allowed';

export interface ChatComposerProps {
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  streaming: boolean;
  pending: PendingAtt[];
  onRemovePending: (id: string) => void;
  imageInputRef: RefObject<HTMLInputElement | null>;
  docInputRef: RefObject<HTMLInputElement | null>;
  onPickFiles: (event: ChangeEvent<HTMLInputElement>) => void;
  voiceLive: boolean;
  onToggleVoice: () => void;
  placeholder?: string;
}

export function ChatComposer({
  draft,
  onDraftChange,
  onSubmit,
  streaming,
  pending,
  onRemovePending,
  imageInputRef,
  docInputRef,
  onPickFiles,
  voiceLive,
  onToggleVoice,
  placeholder = "Posez votre question…",
}: ChatComposerProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useAutoGrowTextarea(draft, COMPOSER_MAX_HEIGHT_PX);
  /* requestSubmit() ignore un bouton submit disabled : sans ce refus explicite,
     le raccourci passerait là où le clic est bloqué. Le garde de fond reste
     celui du handler de soumission — celui-ci ne fait que ne pas le solliciter. */
  const onKeyDown = composerKeyHandler(() => {
    if (streaming) return;
    formRef.current?.requestSubmit();
  });

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className="border-t-[1.5px] border-border [padding:12px_max(16px,env(safe-area-inset-right))_max(12px,env(safe-area-inset-bottom))_max(16px,env(safe-area-inset-left))] bg-card flex-none box-border"
    >
      <div className="max-w-[1060px] mx-auto">
        {pending.length > 0 && (
          <div className="flex gap-2 mb-[10px] flex-wrap">
            {pending.map((p) => (
              <PendingThumb key={p.id} att={p} onRemove={() => onRemovePending(p.id)} />
            ))}
          </div>
        )}

        {/* nowrap + overflow-x:auto = worst-case fallback: if the row can't
            fit (very narrow screen, future extra buttons), it scrolls
            sideways instead of clipping or breaking the layout. */}
        <div className="flex gap-[6px] items-end flex-nowrap overflow-x-auto pb-[2px]">
          <input
            ref={imageInputRef}
            type="file"
            accept={IMAGE_MIMES.join(',')}
            multiple
            onChange={onPickFiles}
            className="hidden"
          />
          <input
            ref={docInputRef}
            type="file"
            accept={DOC_MIMES.join(',')}
            multiple
            onChange={onPickFiles}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={streaming}
            aria-label="Ajouter une photo"
            title="Ajouter une photo"
            className={attachBtnBase}
          >
            📷
          </button>
          <button
            type="button"
            onClick={() => docInputRef.current?.click()}
            disabled={streaming}
            aria-label="Importer un document (PDF, Word, PPT)"
            title="Importer un document (PDF, Word, PPT)"
            className={attachBtnBase}
          >
            📎
          </button>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={placeholder}
            aria-label="Message"
            aria-keyshortcuts="Control+Enter Meta+Enter"
            disabled={streaming}
            className="flex-1 min-w-[120px] max-h-[168px] resize-none px-[14px] py-[11px] border border-border rounded-sm text-[15px] leading-[1.35] font-[inherit] bg-card text-foreground box-border"
          />
          <button
            type="submit"
            aria-label="Envoyer"
            title="Envoyer (Ctrl+Entrée)"
            disabled={streaming || (!draft.trim() && pending.length === 0)}
            className="flex-none w-[44px] h-[44px] p-0 grid place-items-center box-border bg-naia-blue border-none rounded-sm font-extrabold text-[16px] cursor-pointer text-card font-[inherit] disabled:bg-naia-info-bg disabled:text-muted-foreground disabled:cursor-not-allowed"
          >
            {streaming ? '…' : '➤'}
          </button>
          <button
            type="button"
            onClick={onToggleVoice}
            aria-label={voiceLive ? 'Arrêter le vocal' : 'Parler à Naïa'}
            title={voiceLive ? 'Arrêter le vocal' : 'Parler à Naïa'}
            className={cn(voiceBtnBase, voiceLive && 'bg-naia-error text-card border-none')}
          >
            {voiceLive ? '■' : '🎙️'}
          </button>
        </div>
      </div>
      <p className="text-[11.5px] text-muted-foreground text-center mt-2">
        Entrée pour revenir à la ligne, Ctrl+Entrée pour envoyer. Réponses générées par une IA — vérifiez les
        informations importantes.
      </p>
    </form>
  );
}
